import { useEffect, useRef } from "react";
import { ArrowDown } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const ref = useRef(null);
  const orb1 = useRef(null);
  const orb2 = useRef(null);
  const { t } = useT();

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='hero-word']", {
        y: 60, opacity: 0, duration: 1.1, stagger: 0.08, ease: "power3.out",
      });
      gsap.to(orb1.current, { y: 50, x: -30, duration: 9, yoyo: true, repeat: -1, ease: "sine.inOut" });
      gsap.to(orb2.current, { y: -40, x: 30, duration: 11, yoyo: true, repeat: -1, ease: "sine.inOut" });
    }, ref);
    return () => ctx.revert();
  }, []);

  const title = t("hero.title");
  const words = title.split(/\s+/);

  return (
    <div
      ref={ref}
      data-testid={HERO.root}
      className="relative w-full h-screen overflow-hidden grain flex items-center justify-center"
      style={{ background: "var(--ink)" }}
    >
      <div ref={orb1} className="orb" style={{ width: 580, height: 580, background: "radial-gradient(circle, var(--violet) 0%, transparent 70%)", top: "-20%", left: "50%", transform: "translateX(-50%)", opacity: 0.28 }} />
      <div ref={orb2} className="orb" style={{ width: 420, height: 420, background: "radial-gradient(circle, var(--green) 0%, transparent 70%)", bottom: "-18%", left: "50%", transform: "translateX(-50%)", opacity: 0.10 }} />

      <svg className="absolute inset-0 w-full h-full opacity-[0.05]" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <defs>
          <linearGradient id="g1" x1="0" x2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M -100 600 Q 300 200 700 500 T 1400 300" stroke="url(#g1)" strokeWidth="1.5" fill="none" />
        <path d="M -100 700 Q 400 350 800 550 T 1400 450" stroke="url(#g1)" strokeWidth="1" fill="none" />
      </svg>

      <div className="relative z-10 w-full px-6 sm:px-8 md:px-24 lg:px-40 text-center">
        <h1 data-testid={HERO.title} className="h-hero text-white mx-auto" style={{ maxWidth: "60rem" }}>
          {words.map((w, i) => {
            const lower = w.toLowerCase().replace(/[.,!?]/g, "");
            const isEmphasis = ["business", "journey", "bisnis", "perjalanan"].includes(lower);
            return (
              <span key={`${w}-${i}`} className="inline-block mr-[0.32em]" style={{ overflow: "hidden", verticalAlign: "bottom" }}>
                <span
                  data-anim="hero-word"
                  className="inline-block"
                  style={isEmphasis ? { letterSpacing: "2px" } : {}}
                >
                  {w}
                </span>
              </span>
            );
          })}
        </h1>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="eyebrow text-[10px]">{t("hero.scroll")}</span>
        <ArrowDown size={14} className="animate-bounce" />
      </div>
    </div>
  );
}
